import React, { useState, useEffect, useRef } from "react";
import { storeBooking, getAvailablePsikologDetail } from "../api/appointment";
import { useNavigate } from "react-router-dom";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

/* =========================
   HELPER: FORMAT TANGGAL LOKAL (YYYY-MM-DD)
========================== */
const formatDateLocal = (date) => {
  if (!(date instanceof Date)) date = new Date(date);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function BookingDetail({
  bookingData,
  onBack,
  onViewProfile,
  onBookingSuccess,
}) {
  const navigate = useNavigate();
  console.log("Booking Data di BookingDetail:", bookingData);
  const psikolog = bookingData?.psikolog;
  const product = bookingData?.product;

  /* =========================
     STATE MANAGEMENT
  ========================== */
  const [currentSelectedDate, setCurrentSelectedDate] = useState(
    bookingData?.request_date || "",
  );
  const [availableDates, setAvailableDates] = useState(
    bookingData?.available_dates || [],
  );
  const [slots, setSlots] = useState(bookingData?.slots || []);

  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedBahasa, setSelectedBahasa] = useState("");
  const [loading, setLoading] = useState(false);

  const calendarRef = useRef(null);
  const calendarInstance = useRef(null);
  const buttonCalendarRef = useRef(null);

  /* =========================
     INIT FLATPICKR (KALENDER)
  ========================== */
  useEffect(() => {
    if (!calendarRef.current) return;

    calendarInstance.current = flatpickr(calendarRef.current, {
      dateFormat: "Y-m-d",
      minDate: "today",
      positionElement: buttonCalendarRef.current,
      disableMobile: "true",
      defaultDate: currentSelectedDate,
      onChange: async (selectedDates) => {
        if (selectedDates.length > 0) {
          const dateStr = formatDateLocal(selectedDates[0]);
          await handleSelectDate(dateStr);
        }
      },
    });

    return () => calendarInstance.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================
     HANDLER: PILIH TANGGAL (API)
  ========================== */
  const handleSelectDate = async (dateStr) => {
    setLoading(true);
    setSelectedTimeSlot(null);

    try {
      const params = new URLSearchParams(window.location.search);
      const userRole = params.get("user"); // Hasilnya: "admin" atau null
      const res = await getAvailablePsikologDetail({
        psikolog_id: psikolog.id,
        date: dateStr,
        user: userRole,
      });

      if (res.status === true) {
        // Update state dari response BE
        setCurrentSelectedDate(res.data.request_date);
        setAvailableDates(res.data.available_dates || []);
        setSlots(res.data.slots || []);

        // Sinkronkan visual kalender flatpickr
        calendarInstance.current?.setDate(res.data.request_date, false);
      } else {
        alert(res.msg || "Gagal mengambil jadwal");
      }
    } catch (err) {
      alert("Terjadi kesalahan saat mengambil jadwal");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     HANDLER: KONFIRMASI BOOKING
  ========================== */
  const handleConfirmBooking = async () => {
    if (!selectedBahasa) {
      alert("Silakan pilih bahasa konseling terlebih dahulu.");
      return;
    }
    if (!selectedTimeSlot) {
      alert("Silakan pilih jam konseling yang tersedia.");
      return;
    }

    setLoading(true);
    try {
      const res = await storeBooking({
        product_id: product.id,
        project_id: bookingData.project_id,
        student_id: bookingData.participant_id,
        psikolog_id: psikolog.id,
        konseling_date: currentSelectedDate,
        start_time: selectedTimeSlot.start,
        end_time: selectedTimeSlot.end,
        bahasa_konseling: selectedBahasa,
      });

      if (res.status === true) {
        onBookingSuccess?.(res.data);
        navigate("/booking-detail");
      } else {
        alert(res.message || res.msg || "Terjadi kesalahan saat menyimpan jadwal");
      }
    } catch (err) {
      console.log(err);
      alert(
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        "Terjadi kesalahan saat menyimpan jadwal"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     HELPER: AMBIL NAMA HARI
  ========================== */
  const getDayLabel = (dateStr) => {
    const date = new Date(dateStr);
    const todayStr = formatDateLocal(new Date());

    if (dateStr === todayStr) return "Hari Ini";

    const days = [
      "Minggu",
      "Senin",
      "Selasa",
      "Rabu",
      "Kamis",
      "Jumat",
      "Sabtu",
    ];
    return days[date.getDay()];
  };

  const bahasaOptions = psikolog?.bahasa ? psikolog.bahasa.split(",") : [];

  return (
    <div
      className="content mx-auto content-book-detail"
      style={{ maxWidth: "1200px" }}
    >
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          {/* <p className="mt-3 fw-bold text-primary">Mohon tunggu sebentar...</p> */}
        </div>
      )}
      <div className="row g-4">
        {/* SIDEBAR INFO PSIKOLOG */}
        <div className="col-lg-4">
          <div className="card-profile">
            <div className="divider-text">
              <span className="divider-label">Janji temu dengan</span>
            </div>
            <div className="doctor-info d-flex align-items-start mt-3">
              <img
                src={psikolog?.foto}
                alt={psikolog?.name}
                className="me-3"
                style={{
                  borderRadius: "50%",
                  objectFit: "cover",
                  width: "60px",
                  height: "60px",
                }}
              />
              <div>
                <h6 className="mb-0 fw-bold">{psikolog?.name}</h6>
                <p className="text-muted small mb-0">{psikolog?.bahasa}</p>
                <button
                  type="button"
                  className="profile-link small btn btn-link p-0"
                  onClick={onViewProfile}
                >
                  Lihat Profil
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* DETAIL SELECTION */}
        <div className="col-lg-8">
          <div className="booking-detail">
            <div className="main-card">
              <div className="inner-container">
                <h2 className="section-title">Pilih Jadwal Booking</h2>

                {/* PRODUK */}
                <div className="mb-4">
                  <p className="fw-bold mb-3">1. Produk Konseling Anda</p>
                  <p className="fw-bold text-muted">
                    {product?.name} ({product?.duration} menit)
                  </p>
                </div>

                {/* BAHASA */}
                <div className="mb-4">
                  <p className="fw-bold mb-3">2. Pilih Bahasa Konseling</p>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-translate"></i>
                    </span>
                    <select
                      className="form-select form-select-custom"
                      value={selectedBahasa}
                      onChange={(e) => setSelectedBahasa(e.target.value)}
                    >
                      <option value="">Pilih Bahasa</option>
                      {bahasaOptions.map((bhs, i) => (
                        <option key={i} value={bhs.trim()}>
                          {bhs.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 3. PILIH TANGGAL (DINAMIS DARI BE) */}
                <div className="mb-3">
                  <p className="fw-bold mb-3">3. Pilih Tanggal Konseling</p>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {availableDates.map((dateObj, i) => {
                      // Ambil YYYY-MM-DD dari string ISO/Raw yang dikirim BE
                      const rawDateStr = dateObj.raw.split("T")[0];
                      const isActive = rawDateStr === currentSelectedDate;

                      return (
                        <div
                          key={i}
                          className={`slot-btn ${isActive ? "active-slot" : ""}`}
                          onClick={() => handleSelectDate(rawDateStr)}
                          style={{ cursor: "pointer" }}
                        >
                          <span>{getDayLabel(rawDateStr)}</span>
                          <strong>{dateObj.formatted}</strong>
                        </div>
                      );
                    })}

                    <button
                      ref={buttonCalendarRef}
                      className="btn btn-calendar px-4 d-inline-flex align-items-center gap-2"
                      onClick={() => calendarInstance.current?.open()}
                    >
                      <i className="bi bi-calendar3"></i>
                      <div
                        className="text-start"
                        style={{ fontSize: "0.85rem" }}
                      >
                        Lainnya
                      </div>
                    </button>
                    <input
                      ref={calendarRef}
                      type="text"
                      className="d-none"
                      readOnly
                    />
                  </div>

                  <div className="col-12 mt-3">
                    {/* <button
                      ref={buttonCalendarRef}
                      className="btn btn-calendar px-4 d-inline-flex align-items-center gap-2"
                      onClick={() => calendarInstance.current?.open()}
                    >
                      <i className="bi bi-calendar3"></i>
                      <div className="text-start" style={{ fontSize: "0.85rem" }}>
                       Lainnya
                      </div>
                    </button>
                    <input ref={calendarRef} type="text" className="d-none" readOnly /> */}
                  </div>
                </div>

                {/* 4. PILIH WAKTU */}
                <div className="mt-4">
                  <p className="fw-bold mb-3">4. Pilih Waktu Konseling</p>
                  <div className="d-flex flex-wrap gap-2">
                    {slots.length > 0 ? (
                      slots.map((item, i) => (
                        <div
                          key={i}
                          className={`time-btn ${item.status !== "available" ? "booked" : ""} ${selectedTimeSlot?.start === item.start
                              ? "active-slot"
                              : ""
                            }`}
                          onClick={() =>
                            item.status === "available" &&
                            setSelectedTimeSlot(item)
                          }
                        >
                          {item.label}
                        </div>
                      ))
                    ) : (
                      <p className="text-muted small">
                        Tidak ada jam tersedia di tanggal ini.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER BUTTONS */}
      <div className="d-flex justify-content-between mt-4">
        <button className="btn btn-secondary btn-footer" onClick={onBack}>
          <i className="bi bi-arrow-left-short"></i> Kembali
        </button>

        <button
          className="btn btn-primary btn-footer"
          disabled={loading}
          onClick={handleConfirmBooking}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              Memproses...
            </>
          ) : (
            "Konfirmasi Booking"
          )}
        </button>
      </div>
    </div>
  );
}


export default BookingDetail;
